import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import './theme/tokens.css';
import './styles/globals.css';
import i18n, { ensureLanguage } from './i18n/config';
import Auth0ProviderWithNavigate from './context/AuthContext';
import AppearanceRoot from './theme/AppearanceRoot';
import { LiveRegionProvider } from './features/shell/LiveRegion';
import { preferencesStore } from './features/preferences';
import { currentUserKey, userKeyOfToken } from './utils/session';
import { captureBootUrlToken } from './utils/bootUrlToken';
import { httpServices } from './api/client';
import {
  shouldBootPrefetch,
  startBootPrefetch,
} from './features/shell/bootPrefetch';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// A stored (SaaS or dev) session already names its user: paint with that
// user's cached theme and text size instead of the defaults.
// A SaaS entry brings its token in the URL. Read it before any request can
// strip it, adopt it as init will, and use its user (not a stored session's)
// for the cached appearance and the boot prefetch (D-06).
const urlToken = captureBootUrlToken();
if (urlToken) {
  localStorage.setItem('access_token', urlToken);
  httpServices.llmService.updateToken(urlToken);
  httpServices.saasService.updateToken(urlToken);
}
const storedUser = urlToken ? userKeyOfToken(urlToken) : currentUserKey();
if (storedUser) {
  preferencesStore.preview(storedUser);
  // The page's second round of requests, in parallel with /saas/me/info.
  if (shouldBootPrefetch(window.location)) {
    startBootPrefetch(queryClient, storedUser);
  }
}

const render = () =>
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <Auth0ProviderWithNavigate>
          <QueryClientProvider client={queryClient}>
            <AppearanceRoot>
              <LiveRegionProvider>
                <App />
              </LiveRegionProvider>
            </AppearanceRoot>
          </QueryClientProvider>
        </Auth0ProviderWithNavigate>
      </BrowserRouter>
    </StrictMode>,
  );

// An English UI waits for its catalog (a separate chunk) before the first paint.
ensureLanguage(i18n.language).finally(render);
